import { Component, EventEmitter, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { catchError, of, tap } from 'rxjs';
import { SolicitudCamService } from '../../datosbasicos/solicitud-cam.service';
import { TwSolicitudCamViewModelExtended } from '../../../types/solicitudcam-datosbasicos.types';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DialogMode, EnumDescripcionTcTipoSolicitudCamPlural, EnumIdsTcTipoSolicitudCam } from '../../../types/enum';
import { EnumCodigosTwTcAcciones } from 'src/app/components/_importacion/solicitudes/types/enum';
import { NavMenuService } from 'src/app/components/core/components/navmenu/navmenu.service';

@Component({
    selector: 'app-solicitud-recinto-cam-wrapper',
    templateUrl: './solicitud-recinto-cam-wrapper.component.html',
    styleUrls: ['./solicitud-recinto-cam-wrapper.component.scss'],
})
export class SolicitudRecintoCamWrapperComponent extends ComponentBase implements OnInit {
    visibilitySpinner = false;
    visibilitySpinnerGuardando = false;
    recintoListMode = DialogMode.Embedded;

    solicitudCam: TwSolicitudCamViewModelExtended;

    formGroup: FormGroup;
    readonly: boolean;
    private numberOfChanges = 0; // Llevamos la cuenta del número de cambios que ha habido sobre la lista (de recintos "PIADEd")

    onSave$ = new EventEmitter<any>();
    onAdd$ = new EventEmitter<any>();

    private listUrl = '/campanias/solicitudes';

    private activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
        const idSolicitudCam: any = params.get('id');
        if (idSolicitudCam) {
            this.getSolicitudCam(parseInt(idSolicitudCam));
        }
    });

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private solicitudCamService: SolicitudCamService,
        private navMenuService: NavMenuService,
        protected notificacionService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificacionService, exceptionHandler);
    }

    ngOnInit(): void {
        this.buildForm();
    }

    private buildForm(): void {
        this.formGroup = this.fb.group({
            Campania: [null],
            Periodo: [null],
            DescripcionComunidad: [null],
            EntidadAuditora: [null],
            TipoSolicitudCam: [null], // Tipo de recinto
            PeriodoSuperficieMax: [null],
        });
        this.form = this.formGroup;
    }

    /**
     * Este método obtiene los datos de la solicitud indicada por parámetro.
     * Una vez obtenida, establece el valor para la propiedad readonly a partir de la información del usuario y los permisos de edición.
     * Después invoca al servicio de obtención de recintos de la solicitud.
     * @param idSolicitudCam number
     */
    private getSolicitudCam(idSolicitudCam: number): void {
        this.visibilitySpinner = true;
        this.solicitudCamService
            .getSolicitudCamExtended(idSolicitudCam)
            .pipe(
                tap((v: TwSolicitudCamViewModelExtended) => {
                    this.visibilitySpinner = false;
                    this.solicitudCam = v;
                    this.navMenuService.updateMenuServiceForSolicitudCam(v.Id!, v.IdEstadoSolicitudCam!, v.IdTipoSolicitudCam);
                    this.readonly = this.checkReadOnlyConditions();
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.visibilitySpinner = false;
                    this.formGroup.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Hacemos que la ventana de diálogo de selección de recintos (o almacenes) sea visible.
     */
    onClickAdd(): void {
        this.onAdd$.emit();
    }

    onNumberOfChanges(inc: number): void {
        this.numberOfChanges = this.numberOfChanges + inc;
    }

    /**
     * Si result es 0, es que se ha producido un error al guardar.
     * Si result es -1, es que no había cambios.
     * Si result es otro valor, será el id de la solicitud
     * @param result number
     */
    onSaved(result: number): void {
        if (result !== 0) {
            this.numberOfChanges = 0;
        }
        this.visibilitySpinnerGuardando = false;
    }

    onClickSave(): void {
        if (this.numberOfChanges === 0) {
            return;
        }
        this.visibilitySpinnerGuardando = true;
        this.onSave$.emit();
    }

    onBack(): void {
        this.router.navigate([this.listUrl]);
    }

    get esSolicitudDeRecintosPI(): boolean {
        return (
            this.solicitudCam?.IdTipoSolicitudCam === EnumIdsTcTipoSolicitudCam.ID_INVERNADERO ||
            this.solicitudCam?.IdTipoSolicitudCam === EnumIdsTcTipoSolicitudCam.ID_PARCELA
        );
    }

    get esSolicitudDeAlmacen(): boolean {
        return this.solicitudCam?.IdTipoSolicitudCam === EnumIdsTcTipoSolicitudCam.ID_CONFECCION;
    }

    get esSolicitudDeEmpresaDeshidratacion(): boolean {
        return this.solicitudCam?.IdTipoSolicitudCam === EnumIdsTcTipoSolicitudCam.ID_EMPRESA_DESHIDRATACION;
    }

    get savingNotAllowed(): boolean {
        // readonly se calcula en checkReadOnlyConditions, que utiliza loggedInUser?.getAction
        return this.numberOfChanges === 0 || this.visibilitySpinner || this.readonly;
    }

    get addingNotAllowed(): boolean {
        // readonly se calcula en checkReadOnlyConditions, que utiliza loggedInUser?.getAction
        return this.visibilitySpinner || this.readonly;
    }

    get titleRecintoAlmacen(): string {
        if (!this.solicitudCam) return '';

        switch (this.solicitudCam?.IdTipoSolicitudCam) {
            case EnumIdsTcTipoSolicitudCam.ID_CONFECCION:
                return EnumDescripcionTcTipoSolicitudCamPlural.CONFECCION;
            case EnumIdsTcTipoSolicitudCam.ID_EMPRESA_DESHIDRATACION:
                return EnumDescripcionTcTipoSolicitudCamPlural.EMPRESA_DESHIDRATACION;
            case EnumIdsTcTipoSolicitudCam.ID_PARCELA:
                return EnumDescripcionTcTipoSolicitudCamPlural.PARCELA;
            case EnumIdsTcTipoSolicitudCam.ID_INVERNADERO:
                return EnumDescripcionTcTipoSolicitudCamPlural.INVERNADERO;
            default:
                return EnumDescripcionTcTipoSolicitudCamPlural.RECINTOS;
        }
    }

    private get userIsOwner(): boolean {
        return this.isOperadorCam && this.solicitudCam && this.loggedInUser?.Id === this.solicitudCam?.IdUsuario;
    }

    /**
     * Este componente se bloqueará en modo solo lectura cuando el usuario actual no sea admin u Operador de Campaña.
     * Si el usuario no es el propietario y no es el admin, también se bloquea.
     * Además el estado de la solicitud tendrá que ser uno de los permitidos para la edición.
     * @returns boolean
     */
    private checkReadOnlyConditions(): boolean {
        const hasAccessToAdd =
            this.loggedInUser?.getAction(EnumCodigosTwTcAcciones.ADD_REC_A_SOL_CAM, this.solicitudCam.IdEstadoSolicitudCam).Editable || false;
        const hasAccessToDelete =
            this.loggedInUser?.getAction(EnumCodigosTwTcAcciones.REMOVE_REC_DE_SOL_CAM, this.solicitudCam.IdEstadoSolicitudCam).Editable || false;

        if (!hasAccessToAdd || !hasAccessToDelete) {
            return true;
        }

        if (!this.esPeriodoVigente && this.solicitudCam?.PeriodoPresentacionVigente === false) {
            return true;
        }

        if (this.isAdmin) {
            return false;
        }

        return !this.userIsOwner;
    }

    get esPeriodoVigente(): boolean {
        return this.solicitudCam?.PeriodoVigente;
    }
}
